import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Trash2, Users, Calendar, BarChart3, Settings, 
  UserPlus, Lock, Euro, CheckCircle, Clock, ArrowLeft,
  Edit, X, CalendarDays, ChevronLeft, ChevronRight, Mail, Send,
  Search, Filter, Download, Bell, FileText, Eye, Video, MapPin
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import api from '../utils/api';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, parseISO, isValid } from 'date-fns';
import { it } from 'date-fns/locale';

// Email Sender Component for Admin
const AdminEmailSender = () => {
  const [emailType, setEmailType] = useState('confirmation_request');
  const [email, setEmail] = useState('');
  const [clientName, setClientName] = useState('');
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSendEmail = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/send-appointment-email', {
        email,
        email_type: emailType,
        client_name: clientName,
        appointment_date: appointmentDate,
        appointment_time: appointmentTime,
        message
      });
      toast.success(`Email inviata con successo a ${email}`);
      // Reset form
      setEmail('');
      setClientName('');
      setAppointmentDate('');
      setAppointmentTime('');
      setMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Mail className="h-6 w-6 text-secondary" />
        <h3 className="text-xl font-semibold">Invia Email Appuntamento</h3>
      </div>

      <div className="space-y-4">
        {/* Email Type Selection */}
        <div>
          <Label className="text-sm font-medium mb-2 block">Tipo di Email</Label>
          <div className="grid grid-cols-2 gap-3">
            <div
              onClick={() => setEmailType('confirmation_request')}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all text-center ${
                emailType === 'confirmation_request'
                  ? 'border-success bg-success/10'
                  : 'border-muted hover:border-success/50'
              }`}
            >
              <CheckCircle className={`h-6 w-6 mx-auto mb-2 ${emailType === 'confirmation_request' ? 'text-success' : 'text-muted-foreground'}`} />
              <p className="font-medium text-sm">Richiesta Conferma</p>
            </div>
            <div
              onClick={() => setEmailType('modification_request')}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all text-center ${
                emailType === 'modification_request'
                  ? 'border-warning bg-warning/10'
                  : 'border-muted hover:border-warning/50'
              }`}
            >
              <Edit className={`h-6 w-6 mx-auto mb-2 ${emailType === 'modification_request' ? 'text-warning' : 'text-muted-foreground'}`} />
              <p className="font-medium text-sm">Richiesta Modifica</p>
            </div>
          </div>
        </div>

        {/* Email Input */}
        <div>
          <Label htmlFor="email" className="text-sm font-medium">
            Email Destinatario *
          </Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="cliente@email.com"
            className="mt-1"
          />
        </div>

        {/* Client Name */}
        <div>
          <Label htmlFor="clientName" className="text-sm font-medium">
            Nome Cliente
          </Label>
          <Input
            id="clientName"
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            placeholder="Es. Mario Rossi"
            className="mt-1"
          />
        </div>

        {/* Date and Time */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="appointmentDate" className="text-sm font-medium">
              Data Appuntamento
            </Label>
            <Input
              id="appointmentDate"
              type="date"
              value={appointmentDate}
              onChange={(e) => setAppointmentDate(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label htmlFor="appointmentTime" className="text-sm font-medium">
              Ora Appuntamento
            </Label>
            <Input
              id="appointmentTime"
              type="time"
              value={appointmentTime}
              onChange={(e) => setAppointmentTime(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        {/* Message */}
        <div>
          <Label htmlFor="message" className="text-sm font-medium">
            {emailType === 'confirmation_request' ? 'Note aggiuntive' : 'Motivo della modifica'}
          </Label>
          <Textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={emailType === 'confirmation_request' 
              ? 'Eventuali note per il cliente...' 
              : 'Spiega il motivo della richiesta di modifica...'}
            rows={3}
            className="mt-1"
          />
        </div>

        {/* Send Button */}
        <Button
          onClick={handleSendEmail}
          disabled={loading || !email}
          className="w-full bg-secondary text-primary hover:bg-secondary/90"
        >
          {loading ? (
            'Invio in corso...'
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Invia Email
            </>
          )}
        </Button>
      </div>
    </Card>
  );
};

// Bulk Slot Creator Component
const BulkSlotCreator = ({ onSlotsCreated }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDates, setSelectedDates] = useState([]);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('18:00');
  const [loading, setLoading] = useState(false);

  const monthNames = [
    'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
    'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
  ];

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 6 : day - 1; // Convert to Monday = 0
  };

  const isWeekend = (day) => {
    const date = new Date(selectedYear, selectedMonth, day);
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
  };

  const formatDate = (day) => {
    return `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const toggleDate = (day) => {
    const dateStr = formatDate(day);
    if (selectedDates.includes(dateStr)) {
      setSelectedDates(selectedDates.filter(d => d !== dateStr));
    } else {
      setSelectedDates([...selectedDates, dateStr]);
    }
  };

  const selectAllWeekdays = () => {
    const days = getDaysInMonth(selectedMonth, selectedYear);
    const weekdays = [];
    for (let day = 1; day <= days; day++) {
      if (!isWeekend(day)) {
        weekdays.push(formatDate(day));
      }
    }
    setSelectedDates(weekdays);
  };

  const clearSelection = () => {
    setSelectedDates([]);
  };

  const handleBulkCreate = async () => {
    if (selectedDates.length === 0) {
      toast.error('Seleziona almeno una data');
      return;
    }

    if (startTime >= endTime) {
      toast.error('L\'ora di fine deve essere dopo l\'ora di inizio');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/slots/admin/bulk-create', {
        dates: selectedDates.sort(),
        start_time: startTime,
        end_time: endTime
      });
      
      toast.success(`Creati ${response.data.created_count} slot!`);
      if (response.data.skipped_count > 0) {
        toast.info(`${response.data.skipped_count} slot già esistenti saltati`);
      }
      
      setSelectedDates([]);
      if (onSlotsCreated) onSlotsCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione slot');
    } finally {
      setLoading(false);
    }
  };

  const prevMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
    setSelectedDates([]);
  };

  const nextMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
    setSelectedDates([]);
  };

  const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
  const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Calculate hours
  const startHour = parseInt(startTime.split(':')[0]);
  const endHour = parseInt(endTime.split(':')[0]);
  const slotsPerDay = Math.max(0, endHour - startHour);

  return (
    <div className="space-y-6">
      {/* Month Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={prevMonth}>
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <h3 className="text-xl font-semibold">
          {monthNames[selectedMonth]} {selectedYear}
        </h3>
        <Button variant="ghost" onClick={nextMonth}>
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button variant="outline" onClick={selectAllWeekdays} className="flex-1">
          <CalendarDays className="h-4 w-4 mr-2" />
          Seleziona Lun-Ven
        </Button>
        <Button variant="outline" onClick={clearSelection}>
          <X className="h-4 w-4 mr-2" />
          Deseleziona
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="border rounded-lg p-4">
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => (
            <div key={day} className={`text-center text-sm font-medium py-2 ${
              day === 'Sab' || day === 'Dom' ? 'text-muted-foreground' : ''
            }`}>
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {/* Empty cells for days before the first day */}
          {Array.from({ length: firstDay }).map((_, i) => (
            <div key={`empty-${i}`} className="p-2" />
          ))}
          {/* Day cells */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dateStr = formatDate(day);
            const isSelected = selectedDates.includes(dateStr);
            const isWeekendDay = isWeekend(day);
            const isPast = dateStr < todayStr;
            
            return (
              <button
                key={day}
                onClick={() => !isWeekendDay && !isPast && toggleDate(day)}
                disabled={isWeekendDay || isPast}
                className={`p-2 text-center rounded-lg transition-all ${
                  isWeekendDay 
                    ? 'bg-muted/50 text-muted-foreground cursor-not-allowed' 
                    : isPast
                    ? 'bg-muted/30 text-muted-foreground cursor-not-allowed'
                    : isSelected 
                    ? 'bg-primary text-white font-semibold' 
                    : 'hover:bg-secondary/20 cursor-pointer'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Selection */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Ora Inizio</Label>
          <Input
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </div>
        <div>
          <Label>Ora Fine</Label>
          <Input
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </div>
      </div>

      {/* Summary */}
      <div className="bg-muted/30 p-4 rounded-lg">
        <p className="text-sm">
          <span className="font-semibold">{selectedDates.length}</span> giorni selezionati × 
          <span className="font-semibold"> {slotsPerDay}</span> slot/giorno (1 ora ciascuno) = 
          <span className="font-semibold text-primary"> {selectedDates.length * slotsPerDay}</span> slot totali
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Orario: {startTime} - {endTime} (slot da 1 ora)
        </p>
      </div>

      {/* Create Button */}
      <Button 
        onClick={handleBulkCreate} 
        className="w-full bg-primary"
        disabled={loading || selectedDates.length === 0}
      >
        {loading ? 'Creazione in corso...' : `Crea ${selectedDates.length * slotsPerDay} Slot`}
      </Button>
    </div>
  );
};

const AdminPage = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('statistics');
  
  // Data states
  const [slots, setSlots] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [consultationPrice, setConsultationPrice] = useState(50);
  
  // Modification modal state
  const [showModificationModal, setShowModificationModal] = useState(false);
  const [selectedAppointmentForMod, setSelectedAppointmentForMod] = useState(null);
  const [modificationData, setModificationData] = useState({ new_date: '', new_time: '', reason: '' });
  
  // Form states
  const [newSlot, setNewSlot] = useState({ date: '', start_time: '', end_time: '' });
  const [newAdmin, setNewAdmin] = useState({ email: '', password: '', first_name: '', last_name: '' });
  const [passwordChange, setPasswordChange] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [newPaymentRequest, setNewPaymentRequest] = useState({ email: '', client_name: '', description: '', amount: '' });
  
  // Loading states
  const [loading, setLoading] = useState(false);

  // Filter & Search states (NEW)
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPayment, setFilterPayment] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Notes state (NEW)
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedAppointmentForNotes, setSelectedAppointmentForNotes] = useState(null);
  const [appointmentNote, setAppointmentNote] = useState('');

  // Filtered appointments (NEW)
  const filteredAppointments = useMemo(() => {
    return appointments.filter(apt => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        (apt.guest_name || '').toLowerCase().includes(searchLower) ||
        (apt.guest_email || '').toLowerCase().includes(searchLower) ||
        (apt.guest_phone || '').includes(searchTerm);
      
      // Status filter
      const matchesStatus = filterStatus === 'all' || apt.status === filterStatus;
      
      // Payment filter
      const matchesPayment = filterPayment === 'all' || apt.payment_status === filterPayment;
      
      // Type filter
      const matchesType = filterType === 'all' || apt.consultation_type === filterType;
      
      // Date range filter
      let matchesDateRange = true;
      if (filterDateFrom && apt.date < filterDateFrom) matchesDateRange = false;
      if (filterDateTo && apt.date > filterDateTo) matchesDateRange = false;
      
      return matchesSearch && matchesStatus && matchesPayment && matchesType && matchesDateRange;
    });
  }, [appointments, searchTerm, filterStatus, filterPayment, filterType, filterDateFrom, filterDateTo]);

  // Get appointments for a specific date (for calendar view)
  const getAppointmentsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredAppointments.filter(apt => apt.date === dateStr);
  };

  useEffect(() => {
    if (!user || !user.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchAllData();
  }, [user]);

  const fetchAllData = async () => {
    await Promise.all([
      fetchSlots(),
      fetchAppointments(),
      fetchUsers(),
      fetchStatistics(),
      fetchPaymentRequests(),
      fetchSettings()
    ]);
  };

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/settings');
      setConsultationPrice(response.data.consultation_price || 50);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchPaymentRequests = async () => {
    try {
      const response = await api.get('/admin/payment-requests');
      setPaymentRequests(response.data.payment_requests || []);
    } catch (error) {
      console.error('Error fetching payment requests:', error);
    }
  };

  const fetchSlots = async () => {
    try {
      const response = await api.get('/slots/all');
      setSlots(response.data.slots || []);
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  const fetchAppointments = async () => {
    try {
      const response = await api.get('/appointments/admin/all');
      setAppointments(response.data.appointments || []);
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await api.get('/admin/statistics');
      setStatistics(response.data);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

  // Slot management
  const handleCreateSlot = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/slots/admin/create', newSlot);
      toast.success('Slot creato con successo!');
      setNewSlot({ date: '', start_time: '', end_time: '' });
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione slot');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo slot?')) return;
    try {
      await api.delete(`/slots/admin/${slotId}`);
      toast.success('Slot eliminato!');
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione slot');
    }
  };

  // Appointment management
  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Sei sicuro di voler cancellare questo appuntamento?')) return;
    try {
      await api.delete(`/admin/appointments/${appointmentId}`);
      toast.success('Appuntamento cancellato!');
      fetchAppointments();
      fetchSlots();
      fetchStatistics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore cancellazione');
    }
  };

  const handleUpdateAppointmentStatus = async (appointmentId, status) => {
    try {
      await api.patch(`/admin/appointments/${appointmentId}`, { status });
      toast.success('Stato aggiornato!');
      fetchAppointments();
    } catch (error) {
      toast.error('Errore aggiornamento');
    }
  };

  const handleMarkAsPaid = async (appointmentId) => {
    try {
      await api.patch(`/admin/appointments/${appointmentId}`, { payment_status: 'completed' });
      toast.success('Pagamento confermato!');
      fetchAppointments();
      fetchStatistics();
    } catch (error) {
      toast.error('Errore aggiornamento pagamento');
    }
  };

  // Send confirmation request email
  const handleRequestConfirmation = async (appointmentId) => {
    try {
      setLoading(true);
      await api.post(`/admin/appointments/${appointmentId}/request-confirmation`);
      toast.success('Email di richiesta conferma inviata!');
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email');
    } finally {
      setLoading(false);
    }
  };

  // Open modification modal
  const handleOpenModificationModal = (appointment) => {
    setSelectedAppointmentForMod(appointment);
    setModificationData({ new_date: '', new_time: '', reason: '' });
    setShowModificationModal(true);
  };

  // Send modification request email
  const handleSendModificationRequest = async () => {
    if (!modificationData.new_date || !modificationData.new_time) {
      toast.error('Inserisci la nuova data e ora');
      return;
    }
    try {
      setLoading(true);
      await api.post(`/admin/appointments/${selectedAppointmentForMod.id}/request-modification`, modificationData);
      toast.success('Email di richiesta modifica inviata!');
      setShowModificationModal(false);
      setSelectedAppointmentForMod(null);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio email');
    } finally {
      setLoading(false);
    }
  };

  // User management
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo utente e tutti i suoi appuntamenti?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success('Utente eliminato!');
      fetchUsers();
      fetchAppointments();
      fetchStatistics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione utente');
    }
  };

  // Price management
  const handleUpdatePrice = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/admin/settings', { consultation_price: parseFloat(consultationPrice) });
      toast.success('Prezzo aggiornato!');
      fetchStatistics();
    } catch (error) {
      toast.error('Errore aggiornamento prezzo');
    } finally {
      setLoading(false);
    }
  };

  // Payment request management
  const handleCreatePaymentRequest = async (e) => {
    e.preventDefault();
    if (!newPaymentRequest.email || !newPaymentRequest.amount || !newPaymentRequest.client_name) {
      toast.error('Inserisci email, nome cliente e importo');
      return;
    }
    if (!newPaymentRequest.email.includes('@')) {
      toast.error('Inserisci un\'email valida');
      return;
    }
    setLoading(true);
    try {
      await api.post('/admin/payment-request', {
        email: newPaymentRequest.email,
        client_name: newPaymentRequest.client_name,
        description: newPaymentRequest.description || 'Conferimento incarico',
        amount: parseFloat(newPaymentRequest.amount)
      });
      toast.success('Richiesta di pagamento inviata!');
      setNewPaymentRequest({ email: '', client_name: '', description: '', amount: '' });
      fetchPaymentRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione richiesta');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePaymentRequest = async (requestId) => {
    try {
      await api.delete(`/admin/payment-request/${requestId}`);
      toast.success('Richiesta eliminata!');
      fetchPaymentRequests();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Admin creation
  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/admin/create-admin', newAdmin);
      toast.success('Admin creato con successo!');
      setNewAdmin({ email: '', password: '', first_name: '', last_name: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore creazione admin');
    } finally {
      setLoading(false);
    }
  };

  // Password change
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordChange.new_password !== passwordChange.confirm_password) {
      toast.error('Le password non corrispondono');
      return;
    }
    setLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordChange.current_password,
        new_password: passwordChange.new_password
      });
      toast.success('Password cambiata con successo!');
      setPasswordChange({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore cambio password');
    } finally {
      setLoading(false);
    }
  };

  // Daily summary
  const [sendingSummary, setSendingSummary] = useState(false);
  const [summaryDate, setSummaryDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Reminders
  const [sendingReminders, setSendingReminders] = useState(false);

  const handleSendDailySummary = async () => {
    setSendingSummary(true);
    try {
      const response = await api.post(`/admin/send-daily-summary?target_date=${summaryDate}`);
      if (response.data.sent) {
        toast.success(response.data.message);
      } else {
        toast.info(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio riepilogo');
    } finally {
      setSendingSummary(false);
    }
  };

  // Delete admin
  const handleDeleteAdmin = async (adminId, adminEmail) => {
    if (!window.confirm(`Sei sicuro di voler eliminare l'admin ${adminEmail}? Questa azione non può essere annullata.`)) {
      return;
    }
    
    try {
      await api.delete(`/admin/users/${adminId}`);
      toast.success('Admin eliminato con successo');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore eliminazione admin');
    }
  };

  // Send reminders
  const handleSendReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await api.post('/admin/send-reminders');
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore invio promemoria');
    } finally {
      setSendingReminders(false);
    }
  };

  // Save appointment note (NEW)
  const handleSaveNote = async () => {
    if (!selectedAppointmentForNotes) return;
    
    try {
      await api.put(`/admin/appointments/${selectedAppointmentForNotes.id}/note`, {
        note: appointmentNote
      });
      toast.success('Nota salvata con successo');
      setShowNotesModal(false);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore salvataggio nota');
    }
  };

  // Export appointments to CSV (NEW)
  const handleExportCSV = () => {
    const csvData = filteredAppointments.map(apt => ({
      Nome: apt.guest_name || '',
      Email: apt.guest_email || '',
      Telefono: apt.guest_phone || '',
      Data: apt.date || '',
      Ora: apt.start_time || '',
      Tipo: apt.consultation_type === 'video' ? 'Videochiamata' : 'In Presenza',
      Stato: apt.status || '',
      Pagamento: apt.payment_status || '',
      Importo: apt.payment_amount || 0,
      Note: apt.admin_note || ''
    }));

    const headers = Object.keys(csvData[0] || {});
    const csvContent = [
      headers.join(';'),
      ...csvData.map(row => headers.map(h => `"${row[h]}"`).join(';'))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `appuntamenti_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    toast.success('Esportazione completata!');
  };

  // Clear all filters (NEW)
  const clearFilters = () => {
    setSearchTerm('');
    setFilterStatus('all');
    setFilterPayment('all');
    setFilterType('all');
    setFilterDateFrom('');
    setFilterDateTo('');
  };

  if (!user || !user.is_admin) return null;

  const tabs = [
    { id: 'statistics', label: 'Statistiche', icon: BarChart3 },
    { id: 'appointments', label: 'Appuntamenti', icon: Calendar },
    { id: 'slots', label: 'Gestione Slot', icon: Clock },
    { id: 'users', label: 'Utenti', icon: Users },
    { id: 'payments', label: 'Richieste Pagamento', icon: Euro },
    { id: 'admins', label: 'Gestione Admin', icon: UserPlus },
    { id: 'settings', label: 'Impostazioni', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-muted">
      {/* Navbar */}
      <nav className="bg-primary text-white border-b border-secondary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_legal-booking-hub/artifacts/evfhhmo1_logo%20studio%20legale_edited_edited.avif" 
                alt="Studio Legale"
                className="h-14 w-14 object-contain"
              />
              <span className="text-xl font-bold">Pannello Admin</span>
            </div>
            <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-white hover:bg-white/10">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 bg-white p-2 rounded-lg shadow-sm">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? 'default' : 'ghost'}
                onClick={() => setActiveTab(tab.id)}
                className={activeTab === tab.id ? 'bg-primary text-white' : ''}
              >
                <Icon className="h-4 w-4 mr-2" />
                {tab.label}
              </Button>
            );
          })}
        </div>

        {/* Statistics Tab */}
        {activeTab === 'statistics' && statistics && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-6 text-center">
                <Users className="h-8 w-8 mx-auto mb-2 text-primary" />
                <p className="text-3xl font-bold">{statistics.total_users}</p>
                <p className="text-sm text-muted-foreground">Clienti Totali</p>
              </Card>
              <Card className="p-6 text-center">
                <Calendar className="h-8 w-8 mx-auto mb-2 text-secondary" />
                <p className="text-3xl font-bold">{statistics.total_appointments}</p>
                <p className="text-sm text-muted-foreground">Appuntamenti</p>
              </Card>
              <Card className="p-6 text-center">
                <Euro className="h-8 w-8 mx-auto mb-2 text-success" />
                <p className="text-3xl font-bold">€{statistics.total_revenue}</p>
                <p className="text-sm text-muted-foreground">Ricavi Totali</p>
              </Card>
              <Card className="p-6 text-center">
                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-success" />
                <p className="text-3xl font-bold">{statistics.completed_payments}</p>
                <p className="text-sm text-muted-foreground">Pagamenti Completati</p>
              </Card>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="p-6">
                <p className="text-lg font-semibold mb-2">Appuntamenti In Attesa</p>
                <p className="text-4xl font-bold text-warning">{statistics.pending_appointments}</p>
              </Card>
              <Card className="p-6">
                <p className="text-lg font-semibold mb-2">Appuntamenti Confermati</p>
                <p className="text-4xl font-bold text-success">{statistics.confirmed_appointments}</p>
              </Card>
              <Card className="p-6">
                <p className="text-lg font-semibold mb-2">Ultimi 7 Giorni</p>
                <p className="text-4xl font-bold text-primary">{statistics.recent_appointments}</p>
              </Card>
            </div>

            {/* Daily Summary Section */}
            <Card className="p-6 mt-6" data-testid="daily-summary-section">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Mail className="h-5 w-5 text-secondary" />
                Riepilogo Giornaliero
              </h3>
              <p className="text-muted-foreground mb-4">
                Invia un riepilogo via email a tutti gli admin con gli appuntamenti del giorno selezionato.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
                <div className="flex-1">
                  <Label htmlFor="summary-date">Seleziona Data</Label>
                  <Input
                    id="summary-date"
                    type="date"
                    value={summaryDate}
                    onChange={(e) => setSummaryDate(e.target.value)}
                    className="max-w-xs"
                  />
                </div>
                <Button 
                  onClick={handleSendDailySummary}
                  disabled={sendingSummary}
                  className="bg-secondary text-primary hover:bg-secondary/90"
                  data-testid="send-summary-btn"
                >
                  {sendingSummary ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Invia Riepilogo
                    </>
                  )}
                </Button>
              </div>
            </Card>

            {/* Reminder Section */}
            <Card className="p-6 mt-6" data-testid="reminder-section">
              <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                Promemoria Automatici
              </h3>
              <p className="text-muted-foreground mb-4">
                Invia promemoria via email ai clienti con appuntamenti per domani. 
                I promemoria vengono inviati automaticamente ogni giorno alle 9:00.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <Button 
                  onClick={handleSendReminders}
                  disabled={sendingReminders}
                  className="bg-primary text-white hover:bg-primary/90"
                  data-testid="send-reminders-btn"
                >
                  {sendingReminders ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Invio promemoria...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Invia Promemoria per Domani
                    </>
                  )}
                </Button>
                <p className="text-sm text-muted-foreground">
                  Nota: i promemoria già inviati non verranno duplicati
                </p>
              </div>
            </Card>
          </div>
        )}

        {/* Appointments Tab */}
        {activeTab === 'appointments' && (
          <div className="space-y-6">
            {/* Search and Filters Bar */}
            <Card className="p-4">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search */}
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per nome, email o telefono..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="search-appointments"
                  />
                </div>

                {/* View Toggle */}
                <div className="flex gap-2">
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                  >
                    <FileText className="h-4 w-4 mr-1" /> Lista
                  </Button>
                  <Button
                    variant={viewMode === 'calendar' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setViewMode('calendar')}
                  >
                    <CalendarDays className="h-4 w-4 mr-1" /> Calendario
                  </Button>
                </div>

                {/* Export Button */}
                <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv">
                  <Download className="h-4 w-4 mr-2" />
                  Esporta CSV
                </Button>
              </div>

              {/* Filters Row */}
              <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Filtri:</span>
                </div>

                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border rounded px-3 py-1.5 text-sm bg-background"
                >
                  <option value="all">Tutti gli stati</option>
                  <option value="pending">In attesa</option>
                  <option value="confirmed">Confermato</option>
                  <option value="completed">Completato</option>
                  <option value="cancelled">Cancellato</option>
                </select>

                <select
                  value={filterPayment}
                  onChange={(e) => setFilterPayment(e.target.value)}
                  className="border rounded px-3 py-1.5 text-sm bg-background"
                >
                  <option value="all">Tutti i pagamenti</option>
                  <option value="completed">Pagato</option>
                  <option value="pending">In attesa</option>
                  <option value="cash_pending">Contanti</option>
                </select>

                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="border rounded px-3 py-1.5 text-sm bg-background"
                >
                  <option value="all">Tutti i tipi</option>
                  <option value="video">Videochiamata</option>
                  <option value="presence">In Presenza</option>
                </select>

                <Input
                  type="date"
                  value={filterDateFrom}
                  onChange={(e) => setFilterDateFrom(e.target.value)}
                  className="w-auto"
                  placeholder="Da data"
                />
                <Input
                  type="date"
                  value={filterDateTo}
                  onChange={(e) => setFilterDateTo(e.target.value)}
                  className="w-auto"
                  placeholder="A data"
                />

                {(searchTerm || filterStatus !== 'all' || filterPayment !== 'all' || filterType !== 'all' || filterDateFrom || filterDateTo) && (
                  <Button variant="ghost" size="sm" onClick={clearFilters}>
                    <X className="h-4 w-4 mr-1" /> Pulisci filtri
                  </Button>
                )}
              </div>

              {/* Results count */}
              <div className="mt-3 text-sm text-muted-foreground">
                Trovati <span className="font-bold text-primary">{filteredAppointments.length}</span> appuntamenti
              </div>
            </Card>

            {/* Calendar View */}
            {viewMode === 'calendar' && (
              <Card className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <Button variant="outline" onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h3 className="text-xl font-semibold">
                    {format(calendarMonth, 'MMMM yyyy', { locale: it })}
                  </h3>
                  <Button variant="outline" onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => (
                    <div key={day} className="p-2 text-center font-semibold text-muted-foreground text-sm">
                      {day}
                    </div>
                  ))}
                  
                  {eachDayOfInterval({
                    start: startOfMonth(calendarMonth),
                    end: endOfMonth(calendarMonth)
                  }).map((day, idx) => {
                    const dayAppointments = getAppointmentsForDate(day);
                    const dayOfWeek = day.getDay();
                    const offset = idx === 0 ? (dayOfWeek === 0 ? 6 : dayOfWeek - 1) : 0;
                    
                    return (
                      <React.Fragment key={day.toISOString()}>
                        {idx === 0 && Array.from({ length: offset }).map((_, i) => (
                          <div key={`empty-${i}`} className="p-2" />
                        ))}
                        <div 
                          className={`min-h-[80px] p-2 border rounded-lg ${
                            isSameDay(day, new Date()) ? 'bg-secondary/10 border-secondary' : 'bg-muted/20'
                          }`}
                        >
                          <div className="font-medium text-sm mb-1">{format(day, 'd')}</div>
                          <div className="space-y-1">
                            {dayAppointments.slice(0, 3).map(apt => (
                              <div 
                                key={apt.id}
                                className={`text-xs p-1 rounded truncate ${
                                  apt.payment_status === 'completed' 
                                    ? 'bg-success/20 text-success' 
                                    : apt.payment_status === 'cash_pending'
                                    ? 'bg-warning/20 text-warning'
                                    : 'bg-primary/20 text-primary'
                                }`}
                                title={`${apt.start_time} - ${apt.guest_name}`}
                              >
                                {apt.start_time} {apt.guest_name?.split(' ')[0]}
                              </div>
                            ))}
                            {dayAppointments.length > 3 && (
                              <div className="text-xs text-muted-foreground text-center">
                                +{dayAppointments.length - 3} altri
                              </div>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* List View */}
            {viewMode === 'list' && (
            <Card className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3">Cliente</th>
                      <th className="text-left p-3">Data</th>
                      <th className="text-left p-3">Ora</th>
                      <th className="text-left p-3">Tipo</th>
                      <th className="text-left p-3">Stato</th>
                      <th className="text-left p-3">Pagamento</th>
                      <th className="text-left p-3">Importo</th>
                      <th className="text-left p-3">Note</th>
                      <th className="text-left p-3">Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppointments.map((apt) => (
                      <tr key={apt.id} className="border-b hover:bg-muted/30">
                        <td className="p-3">
                          <div>
                            <p className="font-medium">{apt.guest_name || apt.user_name || 'N/A'}</p>
                            {apt.guest_email && (
                              <p className="text-xs text-muted-foreground">{apt.guest_email}</p>
                            )}
                            {apt.guest_phone && (
                              <p className="text-xs text-muted-foreground">{apt.guest_phone}</p>
                            )}
                          </div>
                        </td>
                        <td className="p-3">{apt.date}</td>
                        <td className="p-3">{apt.start_time}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs flex items-center gap-1 w-fit ${
                            apt.consultation_type === 'video' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-green-100 text-green-700'
                          }`}>
                            {apt.consultation_type === 'video' ? <Video className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                            {apt.consultation_type === 'video' ? 'Video' : 'Presenza'}
                          </span>
                        </td>
                        <td className="p-3">
                          <select
                            value={apt.status}
                            onChange={(e) => handleUpdateAppointmentStatus(apt.id, e.target.value)}
                            className="border rounded px-2 py-1 text-sm"
                          >
                            <option value="pending">In attesa</option>
                            <option value="confirmed">Confermato</option>
                            <option value="completed">Completato</option>
                            <option value="cancelled">Cancellato</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs ${
                            apt.payment_status === 'completed' 
                              ? 'bg-success/10 text-success' 
                              : apt.payment_status === 'cash_pending'
                              ? 'bg-warning/10 text-warning'
                              : 'bg-warning/10 text-warning'
                          }`}>
                            {apt.payment_status === 'cash_pending' ? 'Contanti' : apt.payment_status === 'pending' ? 'In attesa' : apt.payment_status === 'completed' ? 'Pagato' : apt.payment_status}
                          </span>
                          {apt.confirmed_by_client && (
                            <span className="ml-2 px-2 py-1 rounded text-xs bg-success/10 text-success">
                              ✓ Confermato
                            </span>
                          )}
                        </td>
                        <td className="p-3 font-semibold">€{apt.payment_amount}</td>
                        <td className="p-3">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedAppointmentForNotes(apt);
                              setAppointmentNote(apt.admin_note || '');
                              setShowNotesModal(true);
                            }}
                            className={apt.admin_note ? 'text-secondary' : 'text-muted-foreground'}
                            title={apt.admin_note || 'Aggiungi nota'}
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {apt.status !== 'confirmed' && apt.guest_email && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleRequestConfirmation(apt.id)}
                                disabled={loading}
                                className="text-blue-600 border-blue-600 hover:bg-blue-50"
                                title="Richiedi conferma via email"
                              >
                                <Mail className="h-4 w-4" />
                              </Button>
                            )}
                            {apt.guest_email && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleOpenModificationModal(apt)}
                                className="text-warning border-warning hover:bg-warning/10"
                                title="Richiedi modifica"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            {apt.payment_status !== 'completed' && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarkAsPaid(apt.id)}
                                className="text-success border-success hover:bg-success/10"
                                title="Segna pagato"
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteAppointment(apt.id)}
                              className="text-destructive border-destructive hover:bg-destructive/10"
                              title="Elimina"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredAppointments.length === 0 && (
                  <p className="text-center py-8 text-muted-foreground">Nessun appuntamento trovato</p>
                )}
              </div>
            </Card>
            )}

            {/* Notes Modal */}
            {showNotesModal && selectedAppointmentForNotes && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <Card className="w-full max-w-md p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Note Private</h3>
                    <Button variant="ghost" size="sm" onClick={() => setShowNotesModal(false)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Cliente: <strong>{selectedAppointmentForNotes.guest_name}</strong>
                  </p>
                  <Textarea
                    value={appointmentNote}
                    onChange={(e) => setAppointmentNote(e.target.value)}
                    placeholder="Aggiungi note private su questo appuntamento..."
                    rows={5}
                  />
                  <div className="flex gap-2 mt-4">
                    <Button variant="outline" onClick={() => setShowNotesModal(false)} className="flex-1">
                      Annulla
                    </Button>
                    <Button onClick={handleSaveNote} className="flex-1 bg-primary">
                      Salva Nota
                    </Button>
                  </div>
                </Card>
              </div>
            )}
            
            {/* Modification Request Modal */}
            {showModificationModal && selectedAppointmentForMod && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                <Card className="w-full max-w-md p-6 m-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-semibold">Richiedi Modifica Appuntamento</h3>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowModificationModal(false)}
                    >
                      <X className="h-5 w-5" />
                    </Button>
                  </div>
                  
                  <div className="mb-4 p-3 bg-muted rounded-lg">
                    <p className="text-sm"><strong>Cliente:</strong> {selectedAppointmentForMod.guest_name}</p>
                    <p className="text-sm"><strong>Data attuale:</strong> {selectedAppointmentForMod.date} alle {selectedAppointmentForMod.start_time}</p>
                    <p className="text-sm"><strong>Email:</strong> {selectedAppointmentForMod.guest_email}</p>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <Label>Nuova Data *</Label>
                      <Input
                        type="date"
                        value={modificationData.new_date}
                        onChange={(e) => setModificationData({...modificationData, new_date: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Nuovo Orario *</Label>
                      <Input
                        type="time"
                        value={modificationData.new_time}
                        onChange={(e) => setModificationData({...modificationData, new_time: e.target.value})}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Motivo della modifica</Label>
                      <Textarea
                        value={modificationData.reason}
                        onChange={(e) => setModificationData({...modificationData, reason: e.target.value})}
                        placeholder="Es. Impegni imprevisti..."
                        rows={3}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button
                        variant="outline"
                        onClick={() => setShowModificationModal(false)}
                        className="flex-1"
                      >
                        Annulla
                      </Button>
                      <Button
                        onClick={handleSendModificationRequest}
                        disabled={loading || !modificationData.new_date || !modificationData.new_time}
                        className="flex-1 bg-secondary text-primary hover:bg-secondary/90"
                      >
                        {loading ? 'Invio...' : 'Invia Richiesta'}
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            )}
          </div>
        )}

        {/* Slots Tab */}
        {activeTab === 'slots' && (
          <div className="space-y-6">
            {/* Bulk Creation Card */}
            <Card className="p-6">
              <h2 className="text-2xl font-semibold mb-6">Creazione Rapida Slot Mensili</h2>
              <BulkSlotCreator onSlotsCreated={fetchSlots} />
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Single Slot Creation */}
              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">Crea Singolo Slot</h2>
                <form onSubmit={handleCreateSlot} className="space-y-4">
                  <div>
                    <Label>Data</Label>
                    <Input
                      type="date"
                      value={newSlot.date}
                      onChange={(e) => setNewSlot({ ...newSlot, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Ora Inizio</Label>
                      <Input
                        type="time"
                        value={newSlot.start_time}
                        onChange={(e) => setNewSlot({ ...newSlot, start_time: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label>Ora Fine</Label>
                      <Input
                        type="time"
                        value={newSlot.end_time}
                        onChange={(e) => setNewSlot({ ...newSlot, end_time: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-secondary text-primary" disabled={loading}>
                    {loading ? 'Creazione...' : 'Crea Slot'}
                  </Button>
                </form>
              </Card>

              {/* Existing Slots */}
              <Card className="p-6">
                <h2 className="text-xl font-semibold mb-4">Slot Esistenti ({slots.length})</h2>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {slots.map((slot) => (
                    <div key={slot.id} className="flex justify-between items-center p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{slot.date}</p>
                        <p className="text-sm text-muted-foreground">{slot.start_time} - {slot.end_time}</p>
                        <span className={`text-xs px-2 py-1 rounded ${
                          slot.is_booked ? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                        }`}>
                          {slot.is_booked ? 'Prenotato' : 'Disponibile'}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteSlot(slot.id)}
                        disabled={slot.is_booked}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  {slots.length === 0 && (
                    <p className="text-center py-8 text-muted-foreground">Nessuno slot creato</p>
                  )}
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <Card className="p-6">
            <h2 className="text-2xl font-semibold mb-6">Utenti Registrati ({users.filter(u => !u.is_admin).length})</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-3">Nome</th>
                    <th className="text-left p-3">Email</th>
                    <th className="text-left p-3">Telefono</th>
                    <th className="text-left p-3">Nazionalità</th>
                    <th className="text-left p-3">Appuntamenti</th>
                    <th className="text-left p-3">Registrato</th>
                    <th className="text-left p-3">Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {users.filter(u => !u.is_admin).map((u) => (
                    <tr key={u.id} className="border-b hover:bg-muted/30">
                      <td className="p-3 font-medium">{u.first_name} {u.last_name}</td>
                      <td className="p-3">{u.email}</td>
                      <td className="p-3">{u.phone || '-'}</td>
                      <td className="p-3">{u.nationality || '-'}</td>
                      <td className="p-3">
                        <span className="bg-primary/10 text-primary px-2 py-1 rounded text-sm">
                          {u.appointment_count}
                        </span>
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString('it-IT')}
                      </td>
                      <td className="p-3">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteUser(u.id)}
                          className="text-destructive border-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.filter(u => !u.is_admin).length === 0 && (
                <p className="text-center py-8 text-muted-foreground">Nessun utente registrato</p>
              )}
            </div>
          </Card>
        )}

        {/* Payment Requests Tab */}
        {activeTab === 'payments' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h2 className="text-2xl font-semibold mb-6">Nuova Richiesta di Pagamento</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Invia una richiesta di pagamento via email. Il cliente riceverà un link per pagare con PayPal (unica soluzione o a rate).
              </p>
              <form onSubmit={handleCreatePaymentRequest} className="space-y-4">
                <div>
                  <Label>Nome Cliente *</Label>
                  <Input
                    value={newPaymentRequest.client_name}
                    onChange={(e) => setNewPaymentRequest({ ...newPaymentRequest, client_name: e.target.value })}
                    placeholder="Es: Mario Rossi"
                    required
                  />
                </div>
                <div>
                  <Label>Email Cliente *</Label>
                  <Input
                    type="email"
                    value={newPaymentRequest.email}
                    onChange={(e) => setNewPaymentRequest({ ...newPaymentRequest, email: e.target.value })}
                    placeholder="Es: mario.rossi@email.com"
                    required
                  />
                </div>
                <div>
                  <Label>Descrizione</Label>
                  <Input
                    value={newPaymentRequest.description}
                    onChange={(e) => setNewPaymentRequest({ ...newPaymentRequest, description: e.target.value })}
                    placeholder="Es: Conferimento incarico - Pratica immigrazione"
                  />
                </div>
                <div>
                  <Label>Importo (€) *</Label>
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    value={newPaymentRequest.amount}
                    onChange={(e) => setNewPaymentRequest({ ...newPaymentRequest, amount: e.target.value })}
                    placeholder="Es: 500.00"
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-secondary text-primary" disabled={loading}>
                  <Euro className="h-4 w-4 mr-2" />
                  {loading ? 'Invio...' : 'Invia Richiesta di Pagamento'}
                </Button>
              </form>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-semibold mb-6">Richieste Inviate ({paymentRequests.length})</h2>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {paymentRequests.map((req) => (
                  <div key={req.id} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold">{req.client_name || req.user_name}</p>
                        <p className="text-sm text-muted-foreground">{req.email || req.user_email}</p>
                        <p className="text-sm mt-1">{req.description}</p>
                        <p className="text-lg font-bold text-primary mt-2">€{req.amount}</p>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          req.status === 'completed' 
                            ? 'bg-success/10 text-success' 
                            : 'bg-warning/10 text-warning'
                        }`}>
                          {req.status === 'completed' ? 'Pagato' : 'In attesa'}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletePaymentRequest(req.id)}
                          className="text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      Creata: {new Date(req.created_at).toLocaleDateString('it-IT')}
                    </p>
                  </div>
                ))}
                {paymentRequests.length === 0 && (
                  <p className="text-center py-8 text-muted-foreground">Nessuna richiesta inviata</p>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Admins Tab */}
        {activeTab === 'admins' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
              <h2 className="text-2xl font-semibold mb-6">Crea Nuovo Admin</h2>
              <form onSubmit={handleCreateAdmin} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nome</Label>
                    <Input
                      value={newAdmin.first_name}
                      onChange={(e) => setNewAdmin({ ...newAdmin, first_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>Cognome</Label>
                    <Input
                      value={newAdmin.last_name}
                      onChange={(e) => setNewAdmin({ ...newAdmin, last_name: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <Input
                    type="password"
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full bg-primary" disabled={loading}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  {loading ? 'Creazione...' : 'Crea Admin'}
                </Button>
              </form>
            </Card>

            <Card className="p-6">
              <h2 className="text-2xl font-semibold mb-6">Admin Esistenti</h2>
              <div className="space-y-3">
                {users.filter(u => u.is_admin).map((admin) => (
                  <div key={admin.id} className="flex justify-between items-center p-4 border rounded-lg bg-primary/5">
                    <div>
                      <p className="font-semibold">{admin.first_name} {admin.last_name}</p>
                      <p className="text-sm text-muted-foreground">{admin.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-primary text-white px-3 py-1 rounded text-sm">Admin</span>
                      {admin.id !== user.id && (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDeleteAdmin(admin.id, admin.email)}
                          data-testid={`delete-admin-${admin.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pricing Settings */}
            <Card className="p-6">
              <h2 className="text-2xl font-semibold mb-6">
                <Euro className="inline h-6 w-6 mr-2" />
                Prezzo Consulenza
              </h2>
              <form onSubmit={handleUpdatePrice} className="space-y-4">
                <div>
                  <Label>Prezzo per consulenza (€)</Label>
                  <Input
                    type="number"
                    min="1"
                    step="0.01"
                    value={consultationPrice}
                    onChange={(e) => setConsultationPrice(e.target.value)}
                    required
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    Questo prezzo verrà applicato a tutte le nuove prenotazioni
                  </p>
                </div>
                <Button type="submit" className="w-full bg-secondary text-primary" disabled={loading}>
                  {loading ? 'Salvataggio...' : 'Aggiorna Prezzo'}
                </Button>
              </form>
            </Card>

            {/* Password Change */}
            <Card className="p-6">
              <h2 className="text-2xl font-semibold mb-6">
                <Lock className="inline h-6 w-6 mr-2" />
                Cambia Password
              </h2>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label>Password Attuale</Label>
                  <Input
                    type="password"
                    value={passwordChange.current_password}
                    onChange={(e) => setPasswordChange({ ...passwordChange, current_password: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Nuova Password</Label>
                  <Input
                    type="password"
                    value={passwordChange.new_password}
                    onChange={(e) => setPasswordChange({ ...passwordChange, new_password: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Conferma Nuova Password</Label>
                  <Input
                    type="password"
                    value={passwordChange.confirm_password}
                    onChange={(e) => setPasswordChange({ ...passwordChange, confirm_password: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? 'Salvataggio...' : 'Cambia Password'}
                </Button>
              </form>
            </Card>

            {/* Email Sender - Full Width */}
            <div className="lg:col-span-2">
              <AdminEmailSender />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
